import WSClient, { type WSClientType } from '@/api/ws';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Ref } from 'vue';

import { fetchSignedInUser } from '@/api/auth';
import type { AutopilotInfo } from '@/api/auth';
import type { Customer, User } from '@/types/types';

export const useUserStore = defineStore('users', () => {
  const currentUser: Ref<User | undefined> = ref();
  const currentCustomer: Ref<Customer | undefined> = ref();
  const isLoggedIn: Ref<boolean> = ref(false);
  const hasValidSession: Ref<boolean> = ref(false);
  // distinguishes "asked the backend, nobody signed in" from "not asked yet"; guards need it since the first navigation's
  // session fetch is still in flight
  const hasResolvedSession: Ref<boolean> = ref(false);

  // single definition of "authenticated" for routing, keyed on holding a user record rather than isLoggedIn,
  // which can be true before the user payload lands
  const isAuthenticated = computed(() => Boolean(currentUser.value?.id));
  /** whether the signed-in account is mid-registration, which keeps it inside the wizard */
  const hasUnfinishedRegistration = computed(() => Boolean(currentUser.value?.registration_step));
  const ws: Ref<WSClientType | undefined> = ref();
  const sandboxUrl: Ref<string | undefined> = ref();
  const googleMapsStaticApiKey: Ref<string | undefined> = ref();
  const supportEmail: Ref<string | undefined> = ref();
  const autopilot: Ref<AutopilotInfo | null> = ref(null);

  /** where an authenticated visitor lands after sign-in: the wizard at the recorded step if unfinished, else the dashboard */
  function authenticatedLandingRoute() {
    const step = currentUser.value?.registration_step;
    if (step) return { name: 'registration', query: { step } };
    return { name: 'home' };
  }

  async function fetchCurrent() {
    try {
      const response = await fetchSignedInUser();
      currentUser.value = response.data as User;
      currentCustomer.value = response.data?.customer?.data as Customer | undefined;
      hasValidSession.value = response.authenticated;
      isLoggedIn.value = response.signed_in;
      hasResolvedSession.value = true;
      sandboxUrl.value = response.sandbox_url;
      googleMapsStaticApiKey.value = response.google_maps_static_api_key;
      supportEmail.value = response.support_email;

      const unfinishedRegistration = Boolean(response.data?.registration_step);

      // WebSocket connect setup
      // NOTE: when user is not signed_in, ws object is NULL
      if (!unfinishedRegistration && response.signed_in && !response.ws) console.error('WebSocket not connected');
      else if (!unfinishedRegistration && response.ws) {
        ws.value = new WSClient(response.ws);
        ws.value.connect();
      }

      // cache autopilot config for lazy connect on first bar open
      if (!unfinishedRegistration) autopilot.value = response.autopilot ?? null;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }

  let pendingResolve: Promise<void> | undefined;

  /** resolves the session once, however many callers (guard, App.vue) ask while the request is in flight */
  function ensureResolved(): Promise<void> {
    if (hasResolvedSession.value) return Promise.resolve();
    pendingResolve ??= fetchCurrent().finally(() => {
      pendingResolve = undefined;
    });
    return pendingResolve;
  }

  return {
    currentUser,
    currentCustomer,
    isLoggedIn,
    sandboxUrl,
    googleMapsStaticApiKey,
    supportEmail,
    hasValidSession,
    hasResolvedSession,
    isAuthenticated,
    hasUnfinishedRegistration,
    authenticatedLandingRoute,
    fetchCurrent,
    ensureResolved,
    ws,
    autopilot,
  };
});

export function getUserFullName(user: User): string {
  return `${user.firstname} ${user.lastname}`;
}
